/**
 * Colour palettes and helpers for text colour and highlight (#279).
 *
 * Every colour that reaches the document goes through sanitizeColor(), both
 * when it is parsed (paste, stored JSON from the API or MCP) and when it is
 * rendered into a style attribute. A colour is written into `style="..."`, so
 * an unchecked value such as `red; background: url(...)` would let stored
 * content inject arbitrary CSS.
 */

export interface ColorSwatch {
  name: string;
  value: string;
}

/**
 * Highlight colours are translucent so the text on top keeps the theme's own
 * colour and stays readable in both dark and light mode.
 */
export const HIGHLIGHT_PRESETS: ColorSwatch[] = [
  { name: 'Yellow', value: '#facc1559' },
  { name: 'Green', value: '#22c55e59' },
  { name: 'Blue', value: '#3b82f659' },
  { name: 'Purple', value: '#a855f759' },
  { name: 'Pink', value: '#ec489959' },
  { name: 'Orange', value: '#f9731659' },
  { name: 'Red', value: '#ef444459' },
  { name: 'Gray', value: '#94a3b859' },
];

/** Mid-tone text colours that read on both the dark and the light theme. */
export const TEXT_COLOR_PRESETS: ColorSwatch[] = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#94a3b8' },
];

/** Preference key holding the user's saved colours as a JSON array of `#rrggbb`. */
export const CUSTOM_COLORS_PREF = 'editor.customColors';
export const MAX_CUSTOM_COLORS = 12;

/** Alpha appended to an opaque custom colour when it is used as a highlight. */
const HIGHLIGHT_ALPHA = '59';

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+|1\.0+)\s*)?\)$/i;

/**
 * Return a safe CSS colour, or null. Accepts hex (3/4/6/8 digits) and the
 * rgb()/rgba() form browsers normalise pasted colours to. Anything else -
 * named colours included - is rejected rather than risk passing through
 * something that is not a colour.
 */
export function sanitizeColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 32) return null;
  if (HEX.test(trimmed)) return trimmed.toLowerCase();
  if (RGB.test(trimmed)) return trimmed.toLowerCase().replace(/\s+/g, '');
  return null;
}

/** Only opaque 6-digit hex is saved as a custom colour (what `<input type="color">` yields). */
function isSavableColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/.test(value);
}

/** Read the saved colours from the raw preference value. Never throws. */
export function parseCustomColors(raw: string | undefined | null): string[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: string[] = [];
  for (const item of parsed) {
    const c = sanitizeColor(item);
    if (c && isSavableColor(c) && !out.includes(c)) out.push(c);
    if (out.length >= MAX_CUSTOM_COLORS) break;
  }
  return out;
}

/** Add a colour to the saved list (newest first), dropping the oldest past the cap. */
export function addCustomColor(list: string[], color: string): string[] {
  const c = sanitizeColor(color);
  if (!c || !isSavableColor(c)) return list;
  return [c, ...list.filter((x) => x !== c)].slice(0, MAX_CUSTOM_COLORS);
}

export function removeCustomColor(list: string[], color: string): string[] {
  const c = sanitizeColor(color);
  return list.filter((x) => x !== c);
}

export function serializeCustomColors(list: string[]): string {
  return JSON.stringify(list);
}

/** Turn an opaque saved colour into the translucent form used for highlights. */
export function toHighlightColor(color: string): string {
  return isSavableColor(color) ? `${color}${HIGHLIGHT_ALPHA}` : color;
}

/** Default length a selection is cut to when it pre-fills a task or note title. */
export const TITLE_MAX_LENGTH = 120;

/**
 * Collapse whitespace and cut a selection down to a usable title, breaking on
 * a word where one is reasonably close to the limit.
 */
export function truncateTitle(text: string, max = TITLE_MAX_LENGTH): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 3);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}...`;
}
